import { memo, ReactElement, Suspense, useCallback, useState } from "react";
import classNames from "classnames";
import { ErrorBoundary } from "react-error-boundary";
import { useImage, useImageProps } from "react-image";
import { Loader } from "../../ui";
import { Lightbox } from "../Lightbox";
import { emptyImage } from "./utils";

import { ReactComponent as PrintIcon } from "./icons/print.svg";

import styles from "./style.module.scss";

const getPrintImageFunction = (link: string) => () => {
  const printWindow = document.createElement("iframe");
  printWindow.style.position = "absolute";
  printWindow.style.top = "-1000px";
  printWindow.style.left = "-1000px";
  printWindow.id = "printImageWindow";
  printWindow.srcdoc = "<!DOCTYPE html>";

  printWindow.onload = () => {
    // Some agents, such as IE11 and Enzyme (as of 2 Jun 2020) continuously call the
    // `onload` callback. This ensures that it is only called once.
    printWindow.onload = null;
    const domDoc = printWindow.contentDocument || printWindow.contentWindow?.document;
    if (domDoc) {
      const img = document.createElement("img");
      img.src = link;
      domDoc.body.appendChild(img);

      setTimeout(async () => {
        if (printWindow.contentWindow) {
          printWindow.contentWindow.focus(); // Needed for IE 11
          await printWindow.contentWindow.print();
          document.body.removeChild(printWindow);
        }
      }, 500);
    }
  };

  document.body.appendChild(printWindow);
};

const ImageComponent = memo<useImageProps & { isOpen: boolean, close: () => void }>((
  {
    close,
    isOpen,
    useSuspense,
    imgPromise,
    srcList,
  }) => {
  let wrappedSrcList: string | string[] = emptyImage;
  if (Array.isArray(srcList)) {
    const filtered = srcList.filter(x => x);
    if (filtered.length) {
      wrappedSrcList = filtered;
    }
  } else if (srcList) {
    wrappedSrcList = srcList;
  }

  const { src } = useImage({
    srcList: wrappedSrcList,
    imgPromise,
    useSuspense,
  });

  const isEmptyImage = src === emptyImage;

  return (
    <>
      <div className={styles.image} style={{ ...(isEmptyImage ? { backgroundSize: "24px" } : {}) }}>
        <img src={src} alt="" />
      </div>
      {
        (isOpen && !isEmptyImage) &&
        <Lightbox
          mainSrc={src || ""}
          toolbarButtons={[
            <button
              className={styles.printButton}
              onClick={getPrintImageFunction(src!)}
            >
              <PrintIcon />
            </button>,
          ]}
          onCloseRequest={close}
        />
      }
    </>
  );
});

export type ImageProps = useImageProps & {
  className?: string,
  error?: ReactElement
}

export const Image = memo<ImageProps>((
  {
    className,
    srcList,
    imgPromise,
    error,
    useSuspense
    , ...props
  }) => {
  const [ isOpen, setIsOpen ] = useState(false);
  const open = useCallback(() => setIsOpen(true), [ setIsOpen ]);
  const close = useCallback(() => setIsOpen(false), [ setIsOpen ]);

  return <button {...props} className={classNames(styles.root, className)} type="button" onClick={open}>
    <div className={classNames(styles.container)}>
      <ErrorBoundary fallbackRender={() => error || <span>Image is corrupted</span>}>
        <Suspense fallback={<Loader />}>
          <ImageComponent
            close={close}
            isOpen={isOpen}
            srcList={srcList}
            imgPromise={imgPromise}
            useSuspense={useSuspense}
          />
        </Suspense>
      </ErrorBoundary>
    </div>
  </button>;
});


export default Image;
